import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { handleRequestHelper } from '../../../utils/helpers';
import api from '../../../api/api';
import { useNotifications } from '../../../Providers/NotificationsProvider/NotificationsProvider';
import { REBATE_TYPE } from '../../../Pages/Backoffice/ProducerPayoutConfigPage/Form/interfaces';

export const useSettlementPreview = (financialOperations) => {
  const { eventId, producerId } = useParams();
  const [settlement, setSettlement] = useState(null);
  const [settlementType, setSettlementType] = useState('final');
  const [payoutSettings, setPayoutSettings] = useState({
    rebatePercentage: '',
    rebateType: REBATE_TYPE.NONE,
    taxId: '',
    taxName: '',
    revenueSharePercentage: '',
  });
  const [settlementRecipientEmails, setSettlementRecipientEmails] = useState([]);
  const [rebateModalOpen, setRebateModalOpen] = useState(false);
  const [settlementModalOpen, setSettlementModalOpen] = useState(false);

  const settlementTypeOptions = [
    { value: 'final', label: 'Final' },
    { value: 'progress', label: 'Parcial' },
  ];

  const onSettlementTypeChange = ({ target: { value } }) => {
    setSettlementType(value);
  };

  const { showNotification } = useNotifications();

  const generateSettlementBody = (isPreview = true, receiverMails = []) => {
    let body = {
      eventId,
      type: settlementType,
    };

    if (settlementType === 'final') {
      body = {
        ...body,
        vatStatus: payoutSettings.vatStatus ?? undefined,
        rebateSettings: {
          rebateType: payoutSettings.rebateType || undefined,
          rebatePercentage: payoutSettings.rebatePercentage || undefined,
          revenueSharePercentage: payoutSettings.revenueSharePercentage || undefined,
        },
      };
    }

    if (!isPreview) {
      body.receiverMails = receiverMails;
    }

    return body;
  };

  const getSettlement = async () => {
    await handleRequestHelper({
      endpoint: () => api.previewSettlement(generateSettlementBody(true)),
      onSuccess: ({ settlement }) => {
        setSettlement(settlement);
      },
      showNotification,
    });
  };

  const getRebateSettings = async () => {
    await handleRequestHelper({
      endpoint: () => api.getProducerPayoutConfig(producerId),
      onSuccess: ({ producerPayoutConfig }) => {
        setPayoutSettings({
          taxId: producerPayoutConfig?.taxId ?? '',
          taxName: producerPayoutConfig?.taxName ?? '',
          vatStatus: producerPayoutConfig?.vatStatus,
          rebatePercentage: producerPayoutConfig?.defaultRebatePercentage ?? '',
          rebateType: producerPayoutConfig?.defaultRebateType ?? REBATE_TYPE.PERCENTAGE,
          revenueSharePercentage: producerPayoutConfig?.defaultRevenueSharePercentage ?? '',
        });
        setSettlementRecipientEmails(producerPayoutConfig?.settlementRecipientEmails ?? []);
      },
      showNotification,
    });
  };

  const updateRebateSettings = async (newSettings) => {
    const percentage =
      newSettings.rebateType === REBATE_TYPE.PERCENTAGE ? newSettings.rebatePercentage : 0;

    await handleRequestHelper({
      endpoint: () => api.updateEventSettings(eventId, { rebatePercentage: percentage }),
      onSuccess: () => {
        setPayoutSettings(newSettings);
      },
      showNotification,
    });
  };

  useEffect(() => {
    if (eventId) {
      getRebateSettings();
    }
  }, [eventId, settlementType]);

  useEffect(() => {
    if (payoutSettings) {
      getSettlement();
    }
  }, [payoutSettings, financialOperations]);

  const sendSettlement = async (receiverMails) => {
    await handleRequestHelper({
      endpoint: () => api.sendSettlement(generateSettlementBody(false, receiverMails)),
      onSuccess: () => {
        showNotification('Liquidación enviada', 'success');
        setSettlementModalOpen(false);
        getSettlement();
      },
      showNotification,
    });
  };

  return {
    settlement,
    settlementType,
    onSettlementTypeChange,
    settlementTypeOptions,
    payoutSettings,
    updateRebateSettings,
    rebateModalOpen,
    setRebateModalOpen,
    settlementModalOpen,
    setSettlementModalOpen,
    settlementRecipientEmails,
    sendSettlement,
  };
};
